// ========================================
// TEAM DETAILS MODAL – REFACTORED
// Enhanced with user management using shared components
// Reduced from 1025 lines to ~650 lines (36% reduction)
// ========================================

import React, { useState, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import { useTranslation } from 'react-i18next';
import { addUserToGroup, removeUserFromGroup, getLiteLLMModelInfo, createOpenWebUIUser, logAuditEntry as logAuditEntryApi } from '../services/api';
import Modal from './shared/Modal';
import ConfirmModal from './shared/ConfirmModal';
import SearchableUserList from './shared/SearchableUserList';
import StatusBadge from './shared/StatusBadge';
import Button from './shared/Button';
import InfoSection from './shared/InfoSection';
import UserDetailsModal from './UserDetailsModal';
import KeyManagementTab from './KeyManagementTab';
import { formatDate, formatDuration } from '../utils/dateUtils';
import {
    getTeamThresholds,
    saveTeamThresholds,
    clearTeamThresholds,
    getGlobalThresholds
} from '../utils/thresholds';
import { logAuditEntry } from '../services/api';

// ── Formatters ────────────────────────────────────────────────────────────────
const fmt2 = v => `$${Number(v || 0).toFixed(2)}`;
const fmtDuration = (duration) => {
    if (!duration) return 'N/A';
    const d = duration.toLowerCase();
    if (d.includes('monthly') || d.includes('30d')) return 'Monthly';
    if (d.includes('yearly') || d.includes('365d')) return 'Yearly';
    return duration;
};

/**
 * TeamDetailsModal Component
 *
 * Displays comprehensive team information and provides management capabilities.
 * Features:
 * - View team details (ID, budget, members, models, rate limits)
 * - Add users to team
 * - Remove users from team
 * - Budget management quick access
 * - Audit logging for all actions
 *
 * @param {Object} team - Team object with details
 * @param {Array} allUsers - Array of all system users
 * @param {Array} allGroups - Array of all OpenWebUI groups
 * @param {Array} allModels - Array of all available models
 * @param {Function} onClose - Callback to close modal
 * @param {Function} onEditQuota - Callback to edit team budget
 * @param {Function} onUpdate - Optional callback after team changes
 */
const TeamDetailsModal = ({
    team,
    allUsers = [],
    allGroups = [],
    allModels = [],
    onClose,
    onEditQuota,
    onUpdate
}) => {
    // ========================================
    // STATE MANAGEMENT
    // ========================================
    const { t } = useTranslation();
    const { accounts } = useMsal();
    const currentUserEmail = accounts[0]?.username || accounts[0]?.email || 'unknown@aware.co.th';

    // Check if current logged-in user is an admin (for showing admin-only features)
    const currentUserIsAdmin = (() => {
        const currentUser = allUsers.find(u =>
            u.email?.toLowerCase() === currentUserEmail?.toLowerCase()
        );
        return currentUser?.role === 'admin';
    })();

    // ========================================
    // ADMIN PROTECTION
    // ========================================
    /**
     * Protected admin email addresses
     * These accounts CANNOT be removed from teams by support staff
     * regardless of their OpenWebUI role setting
     */
    const PROTECTED_ADMIN_EMAILS = [
        'johncattrall@gmail.com',
        'ian.w+admin@aware.co.th',
        'ian.w@aware.co.th',
        'john.c@aware.co.th'
    ];

    /**
     * Check if a user is a protected admin based on their email
     * These admins cannot be removed from teams by support staff
     */
    const isProtectedAdmin = (userEmail) => {
        if (!userEmail) return false;
        return PROTECTED_ADMIN_EMAILS.some(
            email => email.toLowerCase() === userEmail.toLowerCase()
        );
    };

    /**
     * Check if a user is an admin based on their OpenWebUI role
     * The role field from OpenWebUI API indicates admin status
     */
    const isAdminUser = (userRole) => {
        return userRole === 'admin';
    };

    /**
     * Check if the current logged-in user can manage the target user
     * - Protected admins (by email) cannot be removed from teams by anyone
     * - OpenWebUI admins can only manage themselves
     */
    const canManageUser = (targetUser) => {
        // Protected admin emails cannot be removed from teams by anyone
        if (isProtectedAdmin(targetUser.email)) {
            return false;
        }

        const targetIsAdmin = isAdminUser(targetUser.role);
        const currentUserIsTargetUser = targetUser.email?.toLowerCase() === currentUserEmail?.toLowerCase();

        // If target is an OpenWebUI admin, only allow if it's the current user managing themselves
        if (targetIsAdmin) {
            return currentUserIsTargetUser;
        }

        // Non-admin users can be managed by anyone with dashboard access
        return true;
    };

    // Active tab: 'info' | 'members' | 'keys'
    const [activeTab, setActiveTab] = useState('info');

    const [showAddUserModal, setShowAddUserModal] = useState(false);
    const [addingUser, setAddingUser] = useState(false);
    const [removingUserId, setRemovingUserId] = useState(null);
    const [selectedUserForDetails, setSelectedUserForDetails] = useState(null);

    // Create new OpenWebUI user state
    const [showCreateUserForm, setShowCreateUserForm] = useState(false);
    const [createUserForm, setCreateUserForm] = useState({ name: '', email: '', password: '', addToTeam: true });
    const [createUserError, setCreateUserError] = useState('');
    const [creatingUser, setCreatingUser] = useState(false);

    // Confirmation modal states
    const [confirmModal, setConfirmModal] = useState({
        isOpen: false,
        title: '',
        message: '',
        details: '',
        variant: 'primary',
        onConfirm: null
    });
    const [pendingAction, setPendingAction] = useState(null);

    // Alert threshold states
    const [useCustomThresholds, setUseCustomThresholds] = useState(false);
    const [alertThresholdPercent, setAlertThresholdPercent] = useState(90);
    const [blockThresholdPercent, setBlockThresholdPercent] = useState(100);
    const [thresholdsSaved, setThresholdsSaved] = useState(false);

    // Team members search
    const [memberSearchTerm, setMemberSearchTerm] = useState('');

    // Model info modal states
    const [selectedModelName, setSelectedModelName] = useState(null);
    const [modelInfo, setModelInfo] = useState(null);
    const [loadingModelInfo, setLoadingModelInfo] = useState(false);
    const [modelInfoError, setModelInfoError] = useState(null);

    // Load team thresholds on mount
    useEffect(() => {
        if (team?.team_id) {
            const thresholds = getTeamThresholds(team.team_id);
            const globalThresholds = getGlobalThresholds();
            setUseCustomThresholds(thresholds.useCustom);
            setAlertThresholdPercent(Math.round((thresholds.useCustom ? thresholds.budgetAlert : globalThresholds.budgetAlert) * 100));
            setBlockThresholdPercent(Math.round((thresholds.useCustom ? thresholds.budgetBlock : globalThresholds.budgetBlock) * 100));
        }
    }, [team?.team_id]);

    // ========================================
    // ERROR HANDLING
    // ========================================
    if (!team) {
        return (
            <Modal isOpen={true} onClose={onClose} title="Error">
                <p>Team data not available</p>
            </Modal>
        );
    }

    // ========================================
    // DATA EXTRACTION
    // ========================================
    const teamName = team.team_alias || 'Unnamed Team';
    const teamId = team.team_id || 'N/A';
    const currentSpend = team.spend || 0;
    const maxBudget = team.max_budget || 0;
    const budgetDuration = team.budget_duration || 'N/A';
    const budgetResetAt = team.budget_reset_at || null;
    // Filter models: exclude auto-router and similar non-model entries
    const teamModels = team.models || [];
    const models = teamModels.filter(model => {
        const lowerModel = model.toLowerCase();
        // Exclude auto-router and any router/proxy entries
        if (lowerModel.includes('auto-router') || lowerModel.includes('router')) return false;
        return true;
    });
    const blocked = team.blocked || false;
    const maxParallelRequests = team.max_parallel_requests || null;
    const tpmLimit = team.tpm_limit || null;
    const rpmLimit = team.rpm_limit || null;
    const createdAt = team.created_at || null;
    const updatedAt = team.updated_at || null;

    // ========================================
    // TEAM MEMBER MANAGEMENT
    // ========================================

    /**
     * Get current team members from OpenWebUI groups
     */
    const getTeamUsers = () => {
        if (!allGroups || allGroups.length === 0) return [];

        const matchingGroup = allGroups.find(
            g => g.name && g.name.trim().toLowerCase() === teamName.trim().toLowerCase()
        );

        if (!matchingGroup || !matchingGroup.user_ids) return [];

        const userIds = matchingGroup.user_ids;
        const matchedUsers = allUsers.filter(u => userIds.includes(u.id));

        // Keep the original role from allUsers (which comes from OpenWebUI)
        return matchedUsers;
    };

    const teamUsers = getTeamUsers();
    const teamUserIds = teamUsers.map(u => u.id);

    // Filter team members by search term
    const filteredTeamUsers = memberSearchTerm.trim()
        ? teamUsers.filter(user => {
            const search = memberSearchTerm.toLowerCase();
            return (
                (user.name && user.name.toLowerCase().includes(search)) ||
                (user.email && user.email.toLowerCase().includes(search))
            );
        })
        : teamUsers;

    /**
     * Get users that can be added to the team
     */
    const getAvailableUsers = () => {
        return allUsers.filter(user => !teamUserIds.includes(user.id));
    };

    /**
     * Get the matching OpenWebUI group ID for this team
     */
    const getGroupId = () => {
        const matchingGroup = allGroups.find(
            g => g.name && g.name.trim().toLowerCase() === teamName.trim().toLowerCase()
        );
        return matchingGroup?.id || null;
    };

    // ========================================
    // USER MANAGEMENT HANDLERS
    // ========================================

    /**
     * Show confirmation modal for adding a user
     */
    const handleAddUser = (user) => {
        const groupId = getGroupId();
        if (!groupId) {
            alert('Could not find matching group for this team');
            return;
        }

        setPendingAction({ type: 'add', user, groupId });
        setConfirmModal({
            isOpen: true,
            title: 'Add User to Team',
            message: `Add ${user.name || user.email} to team "${teamName}"?`,
            details: 'They will gain access to team resources and models.',
            variant: 'primary',
            onConfirm: () => executeAddUser(user, groupId)
        });
    };

    /**
     * Execute adding a user to the team
     */
    const executeAddUser = async (user, groupId) => {
        setAddingUser(true);

        const result = await addUserToGroup(
            groupId,
            user.id,
            currentUserEmail,
            teamName,
            user.email
        );

        setConfirmModal(prev => ({ ...prev, isOpen: false }));
        setPendingAction(null);

        if (result.success) {
            setShowAddUserModal(false);

            if (onUpdate) {
                await onUpdate();
            }
        } else {
            alert(`Failed to add user: ${result.error}`);
        }

        setAddingUser(false);
    };

    /**
     * Show confirmation modal for removing a user
     */
    const handleRemoveUser = (user) => {
        const groupId = getGroupId();
        if (!groupId) {
            alert('Could not find matching group for this team');
            return;
        }

        setPendingAction({ type: 'remove', user, groupId });
        setConfirmModal({
            isOpen: true,
            title: 'Remove User from Team',
            message: `Remove ${user.name || user.email} from team "${teamName}"?`,
            details: 'This will revoke their access to team resources and models.',
            variant: 'danger',
            onConfirm: () => executeRemoveUser(user, groupId)
        });
    };

    /**
     * Execute removing a user from the team
     */
    const executeRemoveUser = async (user, groupId) => {
        setRemovingUserId(user.id);

        const result = await removeUserFromGroup(
            groupId,
            user.id,
            currentUserEmail,
            teamName,
            user.email
        );

        setConfirmModal(prev => ({ ...prev, isOpen: false }));
        setPendingAction(null);

        if (result.success) {
            if (onUpdate) {
                await onUpdate();
            }
        } else {
            alert(`Failed to remove user: ${result.error}`);
        }

        setRemovingUserId(null);
    };

    // ========================================
    // CREATE USER HANDLER (admin-only)
    // ========================================

    const handleCreateUser = async () => {
        setCreateUserError('');
        const { name, email, password, addToTeam } = createUserForm;
        if (!name.trim()) return setCreateUserError('Full name is required.');
        if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return setCreateUserError('A valid email is required.');
        if (!password || password.length < 8) return setCreateUserError('Password must be at least 8 characters.');

        setCreatingUser(true);
        try {
            const result = await createOpenWebUIUser({ name: name.trim(), email: email.trim().toLowerCase(), password });
            if (!result.success) {
                setCreateUserError(result.error || 'Failed to create user.');
                return;
            }

            // Audit log
            await logAuditEntryApi({
                action: 'create_openwebui_user',
                userEmail: currentUserEmail,
                details: { createdEmail: email.trim().toLowerCase(), createdName: name.trim(), teamName }
            });

            // Optionally add to team group
            if (addToTeam) {
                const groupId = getGroupId();
                const newUserId = result.data?.id;
                if (groupId && newUserId) {
                    await addUserToGroup(groupId, newUserId, currentUserEmail, teamName, email.trim().toLowerCase());
                }
            }

            setShowCreateUserForm(false);
            setCreateUserForm({ name: '', email: '', password: '', addToTeam: true });
            if (onUpdate) await onUpdate();
        } finally {
            setCreatingUser(false);
        }
    };

    // ========================================
    // UTILITY FUNCTIONS
    // ========================================

    const usagePercent = maxBudget > 0 ? Math.round((currentSpend / maxBudget) * 100) : 0;
    const remaining = Math.max(0, maxBudget - currentSpend);
    const isHighUsage = usagePercent >= alertThresholdPercent;

    // ========================================
    // THRESHOLD HANDLERS
    // ========================================

    const handleSaveThresholds = async () => {
        const previousThresholds = getTeamThresholds(team.team_id);

        if (useCustomThresholds) {
            saveTeamThresholds(team.team_id, {
                budgetAlert: alertThresholdPercent / 100,
                budgetBlock: blockThresholdPercent / 100,
                useCustom: true,
            });

            // Log audit entry for custom threshold update
            await logAuditEntry({
                action: 'UPDATE_TEAM_THRESHOLDS',
                userEmail: currentUserEmail,
                details: {
                    teamId: team.team_id,
                    teamName: teamName,
                    previous: {
                        useCustom: previousThresholds.useCustom,
                        budgetAlert: Math.round(previousThresholds.budgetAlert * 100),
                        budgetBlock: Math.round(previousThresholds.budgetBlock * 100),
                    },
                    new: {
                        useCustom: true,
                        budgetAlert: alertThresholdPercent,
                        budgetBlock: blockThresholdPercent,
                    },
                    description: `Set custom thresholds for ${teamName}: Alert ${alertThresholdPercent}%, Block ${blockThresholdPercent}%`
                }
            });
        } else {
            clearTeamThresholds(team.team_id);

            // Log audit entry for reverting to global defaults
            if (previousThresholds.useCustom) {
                await logAuditEntry({
                    action: 'CLEAR_TEAM_THRESHOLDS',
                    userEmail: currentUserEmail,
                    details: {
                        teamId: team.team_id,
                        teamName: teamName,
                        previous: {
                            useCustom: true,
                            budgetAlert: Math.round(previousThresholds.budgetAlert * 100),
                            budgetBlock: Math.round(previousThresholds.budgetBlock * 100),
                        },
                        description: `Reverted ${teamName} to global default thresholds`
                    }
                });
            }
        }
        setThresholdsSaved(true);
        setTimeout(() => setThresholdsSaved(false), 2000);
    };

    const handleToggleCustomThresholds = (enabled) => {
        setUseCustomThresholds(enabled);
        if (!enabled) {
            const globalThresholds = getGlobalThresholds();
            setAlertThresholdPercent(Math.round(globalThresholds.budgetAlert * 100));
            setBlockThresholdPercent(Math.round(globalThresholds.budgetBlock * 100));
        }
    };

    // ========================================
    // MODEL INFO HANDLERS
    // ========================================

    /**
     * Handle clicking on a model to show its details
     */
    const handleModelClick = async (modelName) => {
        setSelectedModelName(modelName);
        setModelInfo(null);
        setModelInfoError(null);
        setLoadingModelInfo(true);

        const result = await getLiteLLMModelInfo(modelName);

        if (result.success) {
            // API returns { data: [...] } - find the matching model by name
            const modelsArray = result.data?.data || [];
            const matchingModel = modelsArray.find(m => m.model_name === modelName);

            if (matchingModel) {
                setModelInfo(matchingModel);
            } else {
                setModelInfoError('Model information not found');
            }
        } else {
            setModelInfoError(result.error || 'Failed to fetch model information');
        }

        setLoadingModelInfo(false);
    };

    /**
     * Close the model info modal
     */
    const closeModelInfoModal = () => {
        setSelectedModelName(null);
        setModelInfo(null);
        setModelInfoError(null);
    };

    /**
     * Format token cost for display
     */
    const formatTokenCost = (cost) => {
        if (cost === null || cost === undefined) return 'N/A';
        if (cost === 0) return '$0.00';
        // Convert to cost per 1M tokens for readability
        const perMillion = cost * 1000000;
        if (perMillion < 0.01) {
            return `$${perMillion.toFixed(6)} / 1M`;
        }
        return `$${perMillion.toFixed(2)} / 1M`;
    };

    // ========================================
    // RENDER HELPERS
    // ========================================

    const renderBudgetInfo = () => (
        <InfoSection
            title={t('teamDetails.budgetInfo')}
            backgroundColor="var(--surface)"
        >
            {maxBudget > 0 ? (
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '0.5rem'
                }}>
                    <BudgetItem
                        label={t('teamDetails.currentSpend')}
                        value={`$${currentSpend.toFixed(2)}`}
                        valueColor={isHighUsage ? '#e74c3c' : '#27ae60'}
                    />
                    <BudgetItem
                        label={t('teamDetails.budgetLimit')}
                        value={`$${maxBudget.toFixed(2)}`}
                    />
                    <BudgetItem
                        label={t('teamDetails.remaining')}
                        value={`$${remaining.toFixed(2)}`}
                        valueColor="#27ae60"
                    />
                    <BudgetItem
                        label={t('teamDetails.usage')}
                        value={`${usagePercent}%`}
                        valueColor={isHighUsage ? '#e74c3c' : '#27ae60'}
                    />
                    <BudgetItem
                        label={t('teamDetails.period')}
                        value={formatDuration(budgetDuration)}
                        isText
                    />
                    <BudgetItem
                        label={t('teamDetails.resets')}
                        value={formatDate(budgetResetAt)}
                        isText
                        small
                    />
                </div>
            ) : (
                <p style={{ color: 'var(--muted)', fontStyle: 'italic', margin: 0 }}>
                    {t('teamDetails.noBudgetConfigured')}
                </p>
            )}
        </InfoSection>
    );

    const renderAlertThresholds = () => (
        <InfoSection
            title={t('teamDetails.alertThresholds')}
            backgroundColor="var(--surface)"
        >
            <div style={{ marginBottom: '1rem' }}>
                <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                    userSelect: 'none'
                }}>
                    <input
                        type="checkbox"
                        checked={useCustomThresholds}
                        onChange={(e) => handleToggleCustomThresholds(e.target.checked)}
                        style={{ marginRight: '0.5rem', width: '16px', height: '16px' }}
                    />
                    <span style={{ color: 'var(--fg-primary)' }}>{t('teamDetails.useCustomThresholds')}</span>
                </label>
                <p style={{
                    fontSize: '0.85rem',
                    color: 'var(--muted)',
                    margin: '0.5rem 0 0 0'
                }}>
                    {useCustomThresholds
                        ? t('teamDetails.customThresholdsNote')
                        : t('teamDetails.usingGlobalDefaults')}
                </p>
            </div>

            <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '1rem',
                opacity: useCustomThresholds ? 1 : 0.5,
                pointerEvents: useCustomThresholds ? 'auto' : 'none'
            }}>
                <div>
                    <label style={{
                        display: 'block',
                        marginBottom: '0.5rem',
                        fontWeight: '500',
                        color: 'var(--fg-secondary)',
                        fontSize: '0.9rem'
                    }}>
                        {t('teamDetails.alertThreshold')}
                        <span style={{ color: 'var(--muted)', fontWeight: 'normal', display: 'block', fontSize: '0.8rem' }}>
                            {t('teamDetails.showWarning')}
                        </span>
                    </label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <input
                            type="number"
                            min="1"
                            max="100"
                            value={alertThresholdPercent}
                            onChange={(e) => setAlertThresholdPercent(Math.max(1, Math.min(100, parseInt(e.target.value) || 90)))}
                            style={{
                                width: '70px',
                                padding: '0.5rem',
                                border: '1px solid var(--border)',
                                borderRadius: '4px',
                                fontSize: '0.95rem',
                                textAlign: 'center',
                                backgroundColor: 'var(--bg)',
                                color: 'var(--fg-primary)'
                            }}
                        />
                        <span style={{ color: 'var(--fg-secondary)' }}>%</span>
                    </div>
                </div>
                <div>
                    <label style={{
                        display: 'block',
                        marginBottom: '0.5rem',
                        fontWeight: '500',
                        color: 'var(--fg-secondary)',
                        fontSize: '0.9rem'
                    }}>
                        {t('teamDetails.blockThreshold')}
                        <span style={{ color: 'var(--muted)', fontWeight: 'normal', display: 'block', fontSize: '0.8rem' }}>
                            {t('teamDetails.autoBlock')}
                        </span>
                    </label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <input
                            type="number"
                            min="1"
                            max="200"
                            value={blockThresholdPercent}
                            onChange={(e) => setBlockThresholdPercent(Math.max(1, Math.min(200, parseInt(e.target.value) || 100)))}
                            style={{
                                width: '70px',
                                padding: '0.5rem',
                                border: '1px solid var(--border)',
                                borderRadius: '4px',
                                fontSize: '0.95rem',
                                textAlign: 'center',
                                backgroundColor: 'var(--bg)',
                                color: 'var(--fg-primary)'
                            }}
                        />
                        <span style={{ color: 'var(--fg-secondary)' }}>%</span>
                    </div>
                </div>
            </div>

            <div style={{ marginTop: '1rem', display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <Button
                    onClick={handleSaveThresholds}
                    variant="primary"
                    size="small"
                >
                    {t('teamDetails.saveThresholds')}
                </Button>
                {thresholdsSaved && (
                    <span style={{ color: 'var(--success)', fontSize: '0.9rem' }}>
                        {t('teamDetails.saved')}
                    </span>
                )}
            </div>
        </InfoSection>
    );

    /**
     * Format Unix timestamp for user display
     */
    const formatUserDate = (timestamp) => {
        if (!timestamp) return 'N/A';
        try {
            const date = new Date(timestamp * 1000);
            return date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            });
        } catch {
            return 'N/A';
        }
    };

    const renderTeamMembers = () => (
        <InfoSection
            title={`${t('teamDetails.teamMembers')} (${teamUsers.length})`}
            headerRight={
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <Button
                        onClick={() => setShowAddUserModal(true)}
                        variant="success"
                        size="small"
                    >
                        {t('teamDetails.addUser')}
                    </Button>
                    {currentUserIsAdmin && (
                        <Button
                            onClick={() => { setShowCreateUserForm(true); setCreateUserError(''); }}
                            variant="primary"
                            size="small"
                        >
                            + Create User
                        </Button>
                    )}
                </div>
            }
        >
            {/* Create User inline form — admin only */}
            {showCreateUserForm && currentUserIsAdmin && (
                <div style={{
                    background: 'var(--bg)',
                    border: '1px solid var(--border)',
                    borderRadius: '8px',
                    padding: '1rem',
                    marginBottom: '1rem'
                }}>
                    <div style={{ fontWeight: 600, color: 'var(--fg-primary)', marginBottom: '0.75rem', fontSize: '0.95rem' }}>
                        Create New OpenWebUI User
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.6rem', marginBottom: '0.6rem' }}>
                        <div>
                            <label style={{ display: 'block', fontSize: '0.8rem', color: 'var(--fg-secondary)', marginBottom: '0.25rem' }}>Full Name *</label>
                            <input
                                type="text"
                                placeholder="Jane Smith"
                                value={createUserForm.name}
                                onChange={e => setCreateUserForm(f => ({ ...f, name: e.target.value }))}
                                style={{ width: '100%', padding: '0.5rem', borderRadius: '5px', border: '1px solid var(--border)', background: 'var(--bg)', color: 'var(--fg-primary)', fontSize: '0.875rem', boxSizing: 'border-box' }}
                            />
                        </div>
                        <div>
                            <label style={{ display: 'block', fontSize: '0.8rem', color: 'var(--fg-secondary)', marginBottom: '0.25rem' }}>Email *</label>
                            <input
                                type="email"
                                placeholder="jane@aware.co.th"
                                value={createUserForm.email}
                                onChange={e => setCreateUserForm(f => ({ ...f, email: e.target.value }))}
                                style={{ width: '100%', padding: '0.5rem', borderRadius: '5px', border: '1px solid var(--border)', background: 'var(--bg)', color: 'var(--fg-primary)', fontSize: '0.875rem', boxSizing: 'border-box' }}
                            />
                        </div>
                        <div>
                            <label style={{ display: 'block', fontSize: '0.8rem', color: 'var(--fg-secondary)', marginBottom: '0.25rem' }}>Temporary Password * (min 8 chars)</label>
                            <input
                                type="password"
                                placeholder="••••••••"
                                value={createUserForm.password}
                                onChange={e => setCreateUserForm(f => ({ ...f, password: e.target.value }))}
                                style={{ width: '100%', padding: '0.5rem', borderRadius: '5px', border: '1px solid var(--border)', background: 'var(--bg)', color: 'var(--fg-primary)', fontSize: '0.875rem', boxSizing: 'border-box' }}
                            />
                        </div>
                        <div style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: '0.1rem' }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', color: 'var(--fg-secondary)', fontSize: '0.875rem' }}>
                                <input
                                    type="checkbox"
                                    checked={createUserForm.addToTeam}
                                    onChange={e => setCreateUserForm(f => ({ ...f, addToTeam: e.target.checked }))}
                                    style={{ width: '15px', height: '15px' }}
                                />
                                Add to this team
                            </label>
                        </div>
                    </div>
                    {createUserError && (
                        <div style={{ color: 'var(--critical)', fontSize: '0.85rem', marginBottom: '0.6rem' }}>{createUserError}</div>
                    )}
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <Button onClick={handleCreateUser} variant="primary" size="small" disabled={creatingUser}>
                            {creatingUser ? 'Creating...' : 'Create User'}
                        </Button>
                        <Button onClick={() => { setShowCreateUserForm(false); setCreateUserForm({ name: '', email: '', password: '', addToTeam: true }); setCreateUserError(''); }} variant="secondary" size="small">
                            Cancel
                        </Button>
                    </div>
                </div>
            )}
            {teamUsers.length > 0 ? (
                <>
                    {/* Search input for team members */}
                    <input
                        type="text"
                        placeholder={t('teamDetails.searchMembers')}
                        value={memberSearchTerm}
                        onChange={(e) => setMemberSearchTerm(e.target.value)}
                        style={{
                            width: '100%',
                            padding: '0.6rem 0.75rem',
                            borderRadius: '6px',
                            border: '1px solid var(--border)',
                            backgroundColor: 'var(--bg)',
                            color: 'var(--fg-primary)',
                            fontSize: '0.9rem',
                            outline: 'none',
                            marginBottom: '0.75rem',
                            boxSizing: 'border-box'
                        }}
                    />
                    <div style={{
                        maxHeight: '300px',
                        overflowY: 'auto',
                        border: '1px solid var(--border)',
                        borderRadius: '6px',
                        backgroundColor: 'var(--surface)'
                    }}>
                        {filteredTeamUsers.length === 0 ? (
                            <div style={{
                                padding: '1.5rem',
                                textAlign: 'center',
                                color: 'var(--muted)'
                            }}>
                                {t('teamDetails.noMembersMatch', { search: memberSearchTerm })}
                            </div>
                        ) : (
                            filteredTeamUsers.map((user, index) => (
                                <div
                                    key={user.id}
                                    style={{
                                        padding: '0.75rem',
                                        backgroundColor: index % 2 === 0 ? 'var(--bg)' : 'var(--surface)',
                                        borderBottom: index < filteredTeamUsers.length - 1
                                            ? '1px solid var(--border)'
                                            : 'none'
                                    }}
                                >
                                    <div style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'flex-start'
                                    }}>
                                        {/* User Info - Clickable */}
                                        <div
                                            style={{
                                                flex: 1,
                                                cursor: 'pointer'
                                            }}
                                            onClick={() => setSelectedUserForDetails(user)}
                                            title="Click to view user details and manage account"
                                        >
                                            <div style={{
                                                fontWeight: '500',
                                                fontSize: '0.9rem',
                                                color: 'var(--accent)',
                                                textDecoration: 'underline',
                                                marginBottom: '0.25rem'
                                            }}>
                                                {user.name || t('userDetails.noName')}
                                            </div>
                                            <div style={{
                                                fontSize: '0.8rem',
                                                color: 'var(--fg-primary)',
                                                marginBottom: '0.25rem'
                                            }}>
                                                {user.email || t('common.noData')}
                                            </div>
                                            <div style={{
                                                fontSize: '0.75rem',
                                                color: 'var(--fg-secondary)',
                                                display: 'flex',
                                                gap: '1rem',
                                                flexWrap: 'wrap'
                                            }}>
                                                <span>{t('teamDetails.role')}: {user.role || 'user'}</span>
                                                <span>{t('teamDetails.created')}: {formatUserDate(user.created_at)}</span>
                                                <span>{t('teamDetails.lastActive')}: {formatUserDate(user.last_active_at)}</span>
                                            </div>
                                        </div>
                                        {/* Action Buttons */}
                                        <div style={{
                                            display: 'flex',
                                            gap: '0.5rem',
                                            alignItems: 'center',
                                            marginLeft: '0.5rem'
                                        }}>
                                            {isAdminUser(user.role) ? (
                                                <span style={{
                                                    padding: '0.25rem 0.5rem',
                                                    backgroundColor: 'var(--border)',
                                                    color: 'var(--fg-primary)',
                                                    borderRadius: '12px',
                                                    fontSize: '0.7rem',
                                                    fontWeight: '600',
                                                    textTransform: 'uppercase'
                                                }}>
                                                    ADMIN
                                                </span>
                                            ) : (
                                                <StatusBadge
                                                    label="USER"
                                                    variant="info"
                                                    size="small"
                                                />
                                            )}
                                            {canManageUser(user) ? (
                                                <Button
                                                    onClick={() => handleRemoveUser(user)}
                                                    variant="danger"
                                                    size="small"
                                                    loading={removingUserId === user.id}
                                                >
                                                    {removingUserId === user.id ? t('teamDetails.removing') : t('teamDetails.remove')}
                                                </Button>
                                            ) : (
                                                <span style={{
                                                    fontSize: '0.75rem',
                                                    color: 'var(--muted)',
                                                    fontStyle: 'italic'
                                                }}>
                                                    {t('teamDetails.protected')}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                    {memberSearchTerm && filteredTeamUsers.length > 0 && (
                        <div style={{
                            marginTop: '0.5rem',
                            fontSize: '0.8rem',
                            color: 'var(--fg-secondary)'
                        }}>
                            {t('teamDetails.showingMembers', { showing: filteredTeamUsers.length, total: teamUsers.length })}
                        </div>
                    )}
                </>
            ) : (
                <p style={{ color: 'var(--muted)', fontStyle: 'italic' }}>
                    {t('teamDetails.noMembers')}
                </p>
            )}
        </InfoSection>
    );

    const renderRateLimits = () => {
        if (!maxParallelRequests && !tpmLimit && !rpmLimit) return null;

        return (
            <InfoSection title={t('teamDetails.rateLimits')}>
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr 1fr',
                    gap: '0.5rem'
                }}>
                    {maxParallelRequests && (
                        <MetricItem label={t('teamDetails.parallel')} value={maxParallelRequests} />
                    )}
                    {tpmLimit && (
                        <MetricItem label="TPM" value={tpmLimit.toLocaleString()} />
                    )}
                    {rpmLimit && (
                        <MetricItem label="RPM" value={rpmLimit.toLocaleString()} />
                    )}
                </div>
            </InfoSection>
        );
    };

    const renderModels = () => (
        <InfoSection title={`${t('teamDetails.models')} (${models.length})`}>
            {models.length > 0 ? (
                <div style={{
                    maxHeight: '200px',
                    overflowY: 'auto',
                    border: '1px solid var(--border)',
                    borderRadius: '6px',
                    padding: '0.75rem',
                    backgroundColor: 'var(--surface)'
                }}>
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                        gap: '0.5rem'
                    }}>
                        {models.map((model, index) => (
                            <div
                                key={index}
                                onClick={() => handleModelClick(model)}
                                style={{
                                    padding: '0.5rem',
                                    backgroundColor: 'var(--surface)',
                                    borderRadius: '4px',
                                    fontSize: '0.85rem',
                                    fontFamily: 'monospace',
                                    color: 'var(--fg-secondary)',
                                    border: '1px solid var(--border)',
                                    cursor: 'pointer',
                                    transition: 'all 0.15s ease'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = 'var(--bg)';
                                    e.currentTarget.style.borderColor = 'var(--border)';
                                    e.currentTarget.style.color = 'var(--fg-primary)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = 'var(--surface)';
                                    e.currentTarget.style.borderColor = 'var(--border)';
                                    e.currentTarget.style.color = 'var(--fg-secondary)';
                                }}
                                title={t('teamDetails.clickToViewModel')}
                            >
                                {model}
                            </div>
                        ))}
                    </div>
                </div>
            ) : (
                <p style={{ color: 'var(--muted)', fontStyle: 'italic' }}>
                    {t('teamDetails.noModels')}
                </p>
            )}
        </InfoSection>
    );

    const renderFooter = () => (
        <>
            <Button
                onClick={onClose}
                variant="secondary"
            >
                {t('common.close')}
            </Button>
            {onEditQuota && maxBudget > 0 && (
                <Button
                    onClick={onEditQuota}
                    variant="primary"
                >
                    {t('teamDetails.manageBudget')}
                </Button>
            )}
            {onEditQuota && maxBudget === 0 && (
                <Button
                    onClick={onEditQuota}
                    variant="success"
                >
                    {t('teamDetails.setBudget')}
                </Button>
            )}
        </>
    );

    // ========================================
    // MAIN RENDER
    // ========================================

    const tabs = [
        { id: 'info', label: 'Info' },
        { id: 'members', label: `Members (${teamUsers.length})` },
        { id: 'keys', label: 'API Keys' }
    ];

    const renderTabNav = () => (
        <div style={{
            display: 'flex',
            borderBottom: '2px solid var(--border)',
            marginBottom: '1.25rem'
        }}>
            {tabs.map(tab => {
                const isActive = activeTab === tab.id;
                return (
                    <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        style={{
                            padding: '0.6rem 1.25rem',
                            background: 'none',
                            border: 'none',
                            borderBottom: isActive ? '2px solid var(--border)' : '2px solid transparent',
                            marginBottom: '-2px',
                            cursor: 'pointer',
                            color: isActive ? 'var(--border)' : 'var(--muted)',
                            fontWeight: isActive ? '600' : '400',
                            fontSize: '0.9rem',
                            transition: 'all 0.15s ease',
                            whiteSpace: 'nowrap'
                        }}
                    >
                        {tab.label}
                    </button>
                );
            })}
        </div>
    );

    return (
        <>
            <Modal
                isOpen={true}
                onClose={onClose}
                title={`${t('teamDetails.title')}: ${teamName}`}
                footer={renderFooter()}
            >
                {renderTabNav()}

                {/* ── INFO TAB ── */}
                {activeTab === 'info' && (
                    <>
                        {/* Team ID & Status */}
                        <div style={{
                            marginBottom: '1.5rem',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        }}>
                            <div style={{ flex: 1 }}>
                                <h3 style={{
                                    fontSize: '1rem',
                                    color: 'var(--border)',
                                    marginBottom: '0.5rem'
                                }}>
                                    {t('teamDetails.teamId')}
                                </h3>
                                <p style={{
                                    fontFamily: 'monospace',
                                    fontSize: '0.85rem',
                                    color: 'var(--fg-secondary)',
                                    wordBreak: 'break-all'
                                }}>
                                    {teamId}
                                </p>
                            </div>
                            <div>
                                <StatusBadge
                                    label={blocked ? t('teamManagement.blocked') : t('teamManagement.active')}
                                    variant={blocked ? 'unhealthy' : 'healthy'}
                                />
                            </div>
                        </div>

                        {/* Models - what the team can access */}
                        {renderModels()}

                        {/* Budget Information */}
                        {renderBudgetInfo()}

                        {/* Alert Thresholds - admin only */}
                        {currentUserIsAdmin && renderAlertThresholds()}

                        {/* Rate Limits - operational limits */}
                        {renderRateLimits()}

                        {/* Timestamps */}
                        <div style={{ marginBottom: 0, fontSize: '0.85rem', color: 'var(--muted)' }}>
                            <div>{t('teamDetails.created')}: {formatDate(createdAt)}</div>
                            <div>{t('teamDetails.updated')}: {formatDate(updatedAt)}</div>
                        </div>
                    </>
                )}

                {/* ── MEMBERS TAB ── */}
                {activeTab === 'members' && renderTeamMembers()}

                {/* ── KEYS TAB ── */}
                {activeTab === 'keys' && (
                    <KeyManagementTab
                        team={team}
                        allModels={allModels}
                        allTeams={[]}
                        allUsers={[]}
                        visibleTeams={[]}
                        onTeamChange={() => {}}
                        NO_TEAM_OPTION={{}}
                        fmt2={fmt2}
                        fmtDuration={fmtDuration}
                        onRefresh={() => {}}
                    />
                )}
            </Modal>

            {/* Add User Modal */}
            {showAddUserModal && (
                <Modal
                    isOpen={true}
                    onClose={() => setShowAddUserModal(false)}
                    title={t('teamDetails.addUserToTeam', { team: teamName })}
                    maxWidth="600px"
                    maxHeight="80vh"
                    footer={
                        <Button
                            onClick={() => setShowAddUserModal(false)}
                            variant="secondary"
                        >
                            {t('common.close')}
                        </Button>
                    }
                >
                    <SearchableUserList
                        users={getAvailableUsers()}
                        onSelectUser={handleAddUser}
                        actionLabel={addingUser ? t('common.loading') : t('common.add')}
                        emptyMessage={t('teamDetails.allUsersInTeam')}
                        emptySearchMessage={t('teamDetails.noUsersMatchSearch')}
                        loading={addingUser}
                    />
                </Modal>
            )}

            {/* User Details Modal - for viewing/managing individual user */}
            {selectedUserForDetails && (
                <UserDetailsModal
                    user={selectedUserForDetails}
                    onClose={() => setSelectedUserForDetails(null)}
                    onUpdate={async () => {
                        // Refresh team data after user account changes
                        if (onUpdate) {
                            await onUpdate();
                        }
                    }}
                />
            )}

            {/* Confirmation Modal */}
            <ConfirmModal
                isOpen={confirmModal.isOpen}
                onClose={() => {
                    setConfirmModal(prev => ({ ...prev, isOpen: false }));
                    setPendingAction(null);
                }}
                onConfirm={confirmModal.onConfirm}
                title={confirmModal.title}
                message={confirmModal.message}
                details={confirmModal.details}
                variant={confirmModal.variant}
                confirmLabel={pendingAction?.type === 'add' ? t('teamManagement.addUser') : t('teamManagement.removeUser')}
                loading={addingUser || removingUserId !== null}
            />

            {/* Model Info Modal */}
            {selectedModelName && (
                <Modal
                    isOpen={true}
                    onClose={closeModelInfoModal}
                    title={t('teamDetails.modelDetails')}
                    maxWidth="500px"
                    footer={
                        <Button onClick={closeModelInfoModal} variant="secondary">
                            {t('common.close')}
                        </Button>
                    }
                >
                    <div style={{ padding: '0.5rem 0' }}>
                        {/* Model Name */}
                        <div style={{ marginBottom: '1.5rem' }}>
                            <div style={{
                                fontSize: '0.85rem',
                                color: 'var(--muted)',
                                marginBottom: '0.25rem'
                            }}>
                                {t('teamDetails.modelName')}
                            </div>
                            <div style={{
                                fontSize: '1.1rem',
                                fontWeight: '600',
                                color: 'var(--border)',
                                fontFamily: 'monospace',
                                wordBreak: 'break-all'
                            }}>
                                {selectedModelName}
                            </div>
                        </div>

                        {loadingModelInfo && (
                            <div style={{
                                textAlign: 'center',
                                padding: '2rem',
                                color: 'var(--muted)'
                            }}>
                                {t('teamDetails.loadingModel')}
                            </div>
                        )}

                        {modelInfoError && (
                            <div style={{
                                padding: '1rem',
                                backgroundColor: 'var(--critical-bg)',
                                border: '1px solid var(--critical)',
                                borderRadius: '6px',
                                color: 'var(--critical)',
                                fontSize: '0.9rem'
                            }}>
                                {modelInfoError}
                            </div>
                        )}

                        {modelInfo && !loadingModelInfo && (() => {
                            // Extract data from both litellm_params and model_info
                            const params = modelInfo.litellm_params || {};
                            const info = modelInfo.model_info || {};

                            // Get costs - prefer litellm_params, fallback to model_info
                            const inputCost = params.input_cost_per_token ?? info.input_cost_per_token;
                            const outputCost = params.output_cost_per_token ?? info.output_cost_per_token;
                            const hasCosts = inputCost != null || outputCost != null;

                            // Get provider info
                            const provider = info.litellm_provider || params.custom_llm_provider;

                            // Get token limits
                            const maxInputTokens = info.max_input_tokens;
                            const maxOutputTokens = info.max_output_tokens || info.max_tokens;
                            const hasTokenLimits = maxInputTokens || maxOutputTokens;

                            // Get capabilities (only those that are true)
                            const capabilities = [];
                            if (info.supports_vision) capabilities.push('Vision');
                            if (info.supports_pdf_input) capabilities.push('PDF Input');
                            if (info.supports_function_calling) capabilities.push('Function Calling');
                            if (info.supports_tool_choice) capabilities.push('Tool Choice');
                            if (info.supports_response_schema) capabilities.push('Response Schema');
                            if (info.supports_prompt_caching) capabilities.push('Prompt Caching');
                            if (info.supports_reasoning) capabilities.push('Reasoning');
                            if (info.supports_web_search) capabilities.push('Web Search');
                            if (info.supports_audio_input) capabilities.push('Audio Input');
                            if (info.supports_audio_output) capabilities.push('Audio Output');
                            if (info.supports_computer_use) capabilities.push('Computer Use');

                            // Get mode
                            const mode = info.mode;

                            return (
                                <div style={{
                                    display: 'grid',
                                    gap: '1rem'
                                }}>
                                    {/* Provider & Mode */}
                                    {(provider || mode) && (
                                        <div style={{
                                            display: 'grid',
                                            gridTemplateColumns: provider && mode ? '1fr 1fr' : '1fr',
                                            gap: '1rem'
                                        }}>
                                            {provider && (
                                                <div style={{
                                                    padding: '1rem',
                                                    backgroundColor: 'var(--surface)',
                                                    borderRadius: '6px'
                                                }}>
                                                    <div style={{
                                                        fontSize: '0.85rem',
                                                        color: 'var(--muted)',
                                                        marginBottom: '0.25rem'
                                                    }}>
                                                        {t('teamDetails.provider')}
                                                    </div>
                                                    <div style={{
                                                        fontSize: '1rem',
                                                        fontWeight: '500',
                                                        color: 'var(--fg-primary)',
                                                        textTransform: 'capitalize'
                                                    }}>
                                                        {provider}
                                                    </div>
                                                </div>
                                            )}
                                            {mode && (
                                                <div style={{
                                                    padding: '1rem',
                                                    backgroundColor: 'var(--surface)',
                                                    borderRadius: '6px'
                                                }}>
                                                    <div style={{
                                                        fontSize: '0.85rem',
                                                        color: 'var(--muted)',
                                                        marginBottom: '0.25rem'
                                                    }}>
                                                        {t('teamDetails.mode')}
                                                    </div>
                                                    <div style={{
                                                        fontSize: '1rem',
                                                        fontWeight: '500',
                                                        color: 'var(--fg-primary)',
                                                        textTransform: 'capitalize'
                                                    }}>
                                                        {mode}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* Token Costs */}
                                    {hasCosts && (
                                        <div style={{
                                            display: 'grid',
                                            gridTemplateColumns: '1fr 1fr',
                                            gap: '1rem'
                                        }}>
                                            {inputCost != null && (
                                                <div style={{
                                                    padding: '1rem',
                                                    backgroundColor: 'var(--surface)',
                                                    borderRadius: '6px'
                                                }}>
                                                    <div style={{
                                                        fontSize: '0.85rem',
                                                        color: 'var(--muted)',
                                                        marginBottom: '0.25rem'
                                                    }}>
                                                        {t('teamDetails.inputCost')}
                                                    </div>
                                                    <div style={{
                                                        fontSize: '1rem',
                                                        fontWeight: '500',
                                                        color: 'var(--success)'
                                                    }}>
                                                        {formatTokenCost(inputCost)}
                                                    </div>
                                                </div>
                                            )}
                                            {outputCost != null && (
                                                <div style={{
                                                    padding: '1rem',
                                                    backgroundColor: 'var(--surface)',
                                                    borderRadius: '6px'
                                                }}>
                                                    <div style={{
                                                        fontSize: '0.85rem',
                                                        color: 'var(--muted)',
                                                        marginBottom: '0.25rem'
                                                    }}>
                                                        {t('teamDetails.outputCost')}
                                                    </div>
                                                    <div style={{
                                                        fontSize: '1rem',
                                                        fontWeight: '500',
                                                        color: 'var(--success)'
                                                    }}>
                                                        {formatTokenCost(outputCost)}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* Token Limits */}
                                    {hasTokenLimits && (
                                        <div style={{
                                            display: 'grid',
                                            gridTemplateColumns: maxInputTokens && maxOutputTokens ? '1fr 1fr' : '1fr',
                                            gap: '1rem'
                                        }}>
                                            {maxInputTokens && (
                                                <div style={{
                                                    padding: '1rem',
                                                    backgroundColor: 'var(--surface)',
                                                    borderRadius: '6px'
                                                }}>
                                                    <div style={{
                                                        fontSize: '0.85rem',
                                                        color: 'var(--muted)',
                                                        marginBottom: '0.25rem'
                                                    }}>
                                                        {t('teamDetails.maxInputTokens')}
                                                    </div>
                                                    <div style={{
                                                        fontSize: '1rem',
                                                        fontWeight: '500',
                                                        color: 'var(--fg-primary)'
                                                    }}>
                                                        {maxInputTokens.toLocaleString()}
                                                    </div>
                                                </div>
                                            )}
                                            {maxOutputTokens && (
                                                <div style={{
                                                    padding: '1rem',
                                                    backgroundColor: 'var(--surface)',
                                                    borderRadius: '6px'
                                                }}>
                                                    <div style={{
                                                        fontSize: '0.85rem',
                                                        color: 'var(--muted)',
                                                        marginBottom: '0.25rem'
                                                    }}>
                                                        {t('teamDetails.maxOutputTokens')}
                                                    </div>
                                                    <div style={{
                                                        fontSize: '1rem',
                                                        fontWeight: '500',
                                                        color: 'var(--fg-primary)'
                                                    }}>
                                                        {maxOutputTokens.toLocaleString()}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* Capabilities */}
                                    {capabilities.length > 0 && (
                                        <div style={{
                                            padding: '1rem',
                                            backgroundColor: 'var(--surface)',
                                            borderRadius: '6px'
                                        }}>
                                            <div style={{
                                                fontSize: '0.85rem',
                                                color: 'var(--muted)',
                                                marginBottom: '0.5rem'
                                            }}>
                                                {t('teamDetails.capabilities')}
                                            </div>
                                            <div style={{
                                                display: 'flex',
                                                flexWrap: 'wrap',
                                                gap: '0.5rem'
                                            }}>
                                                {capabilities.map((cap, idx) => (
                                                    <span
                                                        key={idx}
                                                        style={{
                                                            padding: '0.25rem 0.5rem',
                                                            backgroundColor: 'var(--surface)',
                                                            borderRadius: '4px',
                                                            fontSize: '0.85rem',
                                                            color: 'var(--signal-blue)',
                                                            border: '1px solid var(--signal-blue)'
                                                        }}
                                                    >
                                                        {cap}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })()}
                    </div>
                </Modal>
            )}
        </>
    );
};

// ========================================
// HELPER COMPONENTS
// ========================================

const BudgetItem = ({ label, value, valueColor = 'var(--fg-primary)', isText = false, small = false }) => (
    <div>
        <strong style={{ fontSize: '0.9rem', color: 'var(--muted)' }}>
            {label}:
        </strong>
        <div style={{
            color: valueColor,
            fontSize: isText ? (small ? '0.85rem' : '0.95rem') : '1.1rem',
            fontWeight: isText ? 'normal' : 'bold'
        }}>
            {value}
        </div>
    </div>
);

const MetricItem = ({ label, value }) => (
    <div>
        <strong style={{ fontSize: '0.9rem', color: 'var(--muted)' }}>
            {label}:
        </strong>
        <div style={{
            fontSize: '1rem',
            fontWeight: '500',
            color: 'var(--fg-primary)'
        }}>
            {value}
        </div>
    </div>
);

export default TeamDetailsModal;


